/**
 * PresentationNew — form to generate a new AI presentation.
 * Supports: topic, text, url, file input; outline approval flow; research mode.
 * Route: /app/presentations/new
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, ArrowLeft, Loader2, CheckCircle2, Type, MessageSquare, Upload,
  Users, Palette, Link2, FileText, GripVertical, X, Plus, Search, ChevronRight, ChevronLeft,
  Presentation as PresIcon,
} from "lucide-react";

const SLIDE_QUICK = [5, 8, 10, 12, 15, 20];
const STAGES = [
  { label: "Analysing topic",           duration: 3000 },
  { label: "Researching content",       duration: 6000 },
  { label: "Creating slide outline",    duration: 5000 },
  { label: "Writing slide content",     duration: 8000 },
  { label: "Generating design recs",    duration: 3000 },
  { label: "Building slides",           duration: 3000 },
  { label: "Finalising",                duration: 2000 },
];

const SLIDE_TYPES = [
  "title_slide", "agenda", "content", "two_column", "statistics",
  "quote", "chart", "comparison", "timeline", "thank_you",
];

export default function PresentationNew() {
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [step, setStep] = useState("form"); // form | outline | building
  const [form, setForm] = useState({
    title: "", topic: "", additional_instructions: "", objective: "",
    audience: "General audience", language: "English", num_slides: 8,
    presentation_type: "Business Presentation", tone: "Professional",
    theme_id: "modern-corporate", include_speaker_notes: true,
  });
  const [outline, setOutline] = useState([]);
  const [presTitle, setPresTitle] = useState("");
  const [presSubtitle, setPresSubtitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(-1);
  const [mode, setMode] = useState("topic"); // topic | text | url | file
  const [sourceText, setSourceText] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [researchUrls, setResearchUrls] = useState([""]);
  const [researchOn, setResearchOn] = useState(false);
  const fileRef = useRef(null);
  const [uploadedFile, setUploadedFile] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/presentations/config");
        setCfg(data);
      } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    })();
  }, []);

  useEffect(() => {
    if (!busy) return;
    let idx = 0; setStage(0);
    const step2 = () => {
      if (idx >= STAGES.length - 1) return;
      idx += 1; setStage(idx);
      setTimeout(step2, STAGES[idx].duration);
    };
    const t = setTimeout(step2, STAGES[0].duration);
    return () => clearTimeout(t);
  }, [busy]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) { toast.error("Max 12MB"); return; }
    setUploadedFile({ name: f.name, size: f.size });
    const fd = new FormData(); fd.append("file", f);
    try {
      toast.message("Extracting text…");
      const { data } = await api.post("/presentations/extract-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSourceText(data.text || "");
      toast.success(`Extracted ${data.length} chars from ${data.filename}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to parse file");
      setUploadedFile(null);
    }
  };

  const buildPayload = async () => {
    const payload = { ...form, mode };
    if (mode === "text" || mode === "file") {
      payload.source_text = sourceText;
      if (!payload.topic || payload.topic.length < 3) payload.topic = sourceText.slice(0, 200);
      payload.mode = "text";
    } else if (mode === "url") {
      const { data: extract } = await api.post("/presentations/extract-url", { url: sourceUrl });
      payload.source_url = sourceUrl;
      payload.source_text = extract.text;
      if (!payload.topic || payload.topic.length < 3) payload.topic = "Content from " + sourceUrl;
      payload.mode = "text";
    }
    if (researchOn) {
      const cleaned = researchUrls.map((u) => u.trim()).filter((u) => /^https?:\/\//.test(u));
      if (cleaned.length) payload.research_urls = cleaned;
    }
    return payload;
  };

  const generateOutline = async () => {
    if (mode === "topic" && form.topic.trim().length < 3) { toast.error("Enter a topic (min 3 chars)"); return; }
    if (mode === "text" && sourceText.trim().length < 100) { toast.error("Paste at least 100 characters"); return; }
    if (mode === "url" && !/^https?:\/\//.test(sourceUrl)) { toast.error("Enter a valid URL"); return; }
    if (mode === "file" && !sourceText.trim()) { toast.error("Upload a file first"); return; }
    setBusy(true);
    try {
      const payload = await buildPayload();
      const { data } = await api.post("/presentations/outline", payload);
      setPresTitle(data.presentation_title);
      setPresSubtitle(data.subtitle || "");
      setOutline(data.outline || []);
      setStep("outline");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const buildFromOutline = async () => {
    if (!outline.length) return;
    setBusy(true); setStep("building");
    try {
      const payload = await buildPayload();
      payload.outline = outline;
      payload.presentation_title = presTitle;
      payload.subtitle = presSubtitle;
      const { data } = await api.post("/presentations/from-outline", payload);
      toast.success("Presentation ready!");
      nav(`/app/presentations/${data.id}/edit`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
      setBusy(false); setStep("outline");
    }
  };

  const skipOutlineFullGenerate = async () => {
    // Original single-shot generation, no outline step
    setBusy(true); setStep("building");
    try {
      const payload = await buildPayload();
      const { data } = await api.post("/presentations", payload);
      toast.success("Presentation ready!");
      nav(`/app/presentations/${data.id}/edit`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
      setBusy(false); setStep("form");
    }
  };

  // Outline editing
  const editOutlineItem = (i, patch) => {
    setOutline((o) => o.map((it, j) => j === i ? { ...it, ...patch } : it));
  };
  const moveItem = (i, dir) => {
    setOutline((o) => {
      const arr = [...o];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr.map((x, k) => ({ ...x, slide_number: k + 1 }));
    });
  };
  const deleteItem = (i) => {
    setOutline((o) => o.filter((_, j) => j !== i).map((x, k) => ({ ...x, slide_number: k + 1 })));
  };
  const addItem = () => {
    setOutline((o) => {
      const n = o.length + 1;
      return [...o, { slide_number: n, slide_type: "content", title: "New Slide",
                      description: "Describe what this slide will cover", visual_type: "none" }];
    });
  };

  // -------------------- Building screen --------------------
  if (step === "building") {
    return (
      <div className="max-w-2xl mx-auto vp-card p-8 vp-fade-up text-center space-y-6">
        <div className="inline-flex items-center gap-2 vp-chip bg-[#7C3AED]/10 text-[#7C3AED]">
          <Sparkles className="w-3 h-3" /> AI is thinking
        </div>
        <h2 className="font-display font-black text-2xl">Building your presentation…</h2>
        <div className="space-y-2 text-left max-w-md mx-auto">
          {STAGES.map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              {i < stage ? <CheckCircle2 className="w-4 h-4 text-[#059669] shrink-0" /> :
                i === stage ? <Loader2 className="w-4 h-4 text-[#0B5CFF] animate-spin shrink-0" /> :
                <div className="w-4 h-4 rounded-full border border-[#E4E6EC] shrink-0" />}
              <span className={i <= stage ? "text-[#0B0D17]" : "text-[#747680]"}>{s.label}</span>
            </div>
          ))}
        </div>
        <div className="text-xs text-[#747680]">Usually 20–40s. Kimi generates structured JSON that maps to real editable slides.</div>
      </div>
    );
  }

  // -------------------- Outline approval screen --------------------
  if (step === "outline") {
    return (
      <div className="max-w-3xl mx-auto space-y-4 vp-fade-up">
        <div className="flex items-center gap-2">
          <button onClick={() => setStep("form")} className="p-2 rounded-lg hover:bg-[#F1F3F7]" data-testid="outline-back-btn"><ArrowLeft className="w-4 h-4" /></button>
          <div>
            <div className="text-xs text-[#7C3AED] font-semibold uppercase tracking-wider">Step 2 of 2 — Approve outline</div>
            <h1 className="font-display font-black text-xl sm:text-2xl">Review before we build the deck</h1>
          </div>
        </div>
        <div className="vp-card p-4 space-y-3">
          <div>
            <label className="vp-label">Deck title</label>
            <input className="vp-input font-display font-bold text-lg" value={presTitle} onChange={(e) => setPresTitle(e.target.value)} data-testid="outline-title" />
          </div>
          <div>
            <label className="vp-label">Subtitle</label>
            <input className="vp-input" value={presSubtitle} onChange={(e) => setPresSubtitle(e.target.value)} data-testid="outline-subtitle" />
          </div>
        </div>
        <div className="vp-card p-3">
          <div className="text-xs font-semibold mb-2 flex items-center justify-between">
            <span>{outline.length} slides — drag or use arrows to reorder</span>
            <button onClick={addItem} className="text-xs text-[#7C3AED] hover:underline inline-flex items-center gap-1" data-testid="add-outline-item"><Plus className="w-3 h-3" /> Add slide</button>
          </div>
          <div className="space-y-2">
            {outline.map((it, i) => (
              <div key={i} className="border border-[#E4E6EC] rounded-lg p-2 flex items-start gap-2" data-testid={`outline-item-${i+1}`}>
                <div className="flex flex-col gap-0.5 pt-1">
                  <button onClick={() => moveItem(i, -1)} disabled={i === 0} className="p-0.5 disabled:opacity-30 hover:bg-[#F1F3F7] rounded" title="Move up"><ChevronLeft className="w-3 h-3 rotate-90" /></button>
                  <button onClick={() => moveItem(i, +1)} disabled={i === outline.length - 1} className="p-0.5 disabled:opacity-30 hover:bg-[#F1F3F7] rounded" title="Move down"><ChevronRight className="w-3 h-3 rotate-90" /></button>
                </div>
                <div className="text-xs text-[#747680] font-mono w-6 shrink-0 pt-2">{i + 1}</div>
                <div className="flex-1 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <input
                      className="vp-input py-1.5 text-sm font-semibold flex-1"
                      value={it.title}
                      onChange={(e) => editOutlineItem(i, { title: e.target.value })}
                      data-testid={`outline-title-${i+1}`}
                    />
                    <select
                      className="vp-input py-1.5 text-xs w-32"
                      value={it.slide_type}
                      onChange={(e) => editOutlineItem(i, { slide_type: e.target.value })}
                    >
                      {SLIDE_TYPES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <textarea
                    className="vp-input py-1.5 text-xs"
                    rows={1}
                    value={it.description}
                    onChange={(e) => editOutlineItem(i, { description: e.target.value })}
                    placeholder="What will this slide cover?"
                  />
                </div>
                <button onClick={() => deleteItem(i)} className="p-1 text-[#B00020] hover:bg-[#F1F3F7] rounded" title="Delete"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between gap-2">
          <button onClick={() => setStep("form")} className="vp-btn-ghost text-sm py-2 px-3" data-testid="outline-cancel">Back to form</button>
          <button onClick={buildFromOutline} disabled={busy || !outline.length} className="vp-btn-primary text-sm py-2 px-4 inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="outline-approve">
            <Sparkles className="w-4 h-4" /> Build full deck ({outline.length} slides)
          </button>
        </div>
      </div>
    );
  }

  // -------------------- Form screen --------------------
  return (
    <div className="max-w-3xl mx-auto space-y-4 vp-fade-up">
      <div className="flex items-center gap-2">
        <button onClick={() => nav("/app/presentations")} className="p-2 rounded-lg hover:bg-[#F1F3F7]" data-testid="new-pres-back"><ArrowLeft className="w-4 h-4" /></button>
        <h1 className="font-display font-black text-xl sm:text-2xl">Create New Presentation</h1>
      </div>

      {/* MODE tabs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { id: "topic", label: "From Topic", icon: Type, desc: "Just describe it" },
          { id: "text",  label: "From Text",  icon: MessageSquare, desc: "Paste article/notes" },
          { id: "url",   label: "From URL",   icon: Link2, desc: "Blog/webpage link" },
          { id: "file",  label: "From File",  icon: FileText, desc: "PDF / DOCX / CSV / XLSX" },
        ].map((m) => (
          <button
            key={m.id}
            onClick={() => setMode(m.id)}
            className={"vp-card p-3 text-left transition " + (mode === m.id ? "border-[#7C3AED] bg-[#7C3AED]/5" : "")}
            data-testid={`mode-${m.id}`}
          >
            <m.icon className={"w-4 h-4 mb-1 " + (mode === m.id ? "text-[#7C3AED]" : "text-[#4A4D57]")} />
            <div className="font-display font-semibold text-sm">{m.label}</div>
            <div className="text-[10px] text-[#747680]">{m.desc}</div>
          </button>
        ))}
      </div>

      <div className="vp-card p-5 space-y-4">
        {mode === "text" && (
          <div>
            <label className="vp-label">Paste your content (article, notes, script, meeting minutes…)</label>
            <textarea
              className="vp-input"
              rows={7}
              value={sourceText}
              onChange={(e) => setSourceText(e.target.value)}
              placeholder="Paste 100+ characters of content."
              data-testid="source-text"
            />
            <div className="text-[10px] text-[#747680] mt-1">{sourceText.length} chars — {sourceText.length < 100 ? "min 100 required" : "looks good"}</div>
          </div>
        )}
        {mode === "url" && (
          <div>
            <label className="vp-label">Web page URL</label>
            <input
              className="vp-input"
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              placeholder="https://blog.example.com/article"
              data-testid="source-url"
            />
            <div className="text-[10px] text-[#747680] mt-1">We'll extract the readable text.</div>
          </div>
        )}
        {mode === "file" && (
          <div>
            <label className="vp-label">Upload a file (PDF / DOCX / CSV / XLSX / TXT, max 12MB)</label>
            <input ref={fileRef} type="file" accept=".pdf,.docx,.csv,.xlsx,.xls,.txt,.md" onChange={onFilePick} className="hidden" />
            <button onClick={() => fileRef.current?.click()} className="vp-btn-ghost text-sm py-2 px-3 inline-flex items-center gap-2" data-testid="upload-file-btn">
              <Upload className="w-4 h-4" /> Choose file
            </button>
            {uploadedFile && (
              <div className="mt-2 text-xs text-[#4A4D57] flex items-center gap-2">
                <FileText className="w-3.5 h-3.5" />
                <span className="truncate">{uploadedFile.name} ({(uploadedFile.size / 1024).toFixed(1)} KB)</span>
                <span className="text-[#059669]">{sourceText.length} chars extracted</span>
              </div>
            )}
          </div>
        )}
        <div>
          <label className="vp-label">Topic <span className="text-[#B00020]">*</span></label>
          <input
            className="vp-input"
            value={form.topic}
            onChange={(e) => set("topic", e.target.value)}
            placeholder={mode === "topic" ? "e.g., Digital Marketing Strategy for D2C Brands" : "Optional short topic to guide the AI"}
            data-testid="new-topic"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="vp-label">Title (optional)</label>
            <input className="vp-input" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Auto-generate if empty" data-testid="new-title" />
          </div>
          <div>
            <label className="vp-label">Objective</label>
            <input className="vp-input" value={form.objective} onChange={(e) => set("objective", e.target.value)} placeholder="Inform, persuade, train…" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="vp-label inline-flex items-center gap-1"><Users className="w-3 h-3" /> Target audience</label>
            <input className="vp-input" value={form.audience} onChange={(e) => set("audience", e.target.value)} data-testid="new-audience" />
          </div>
          <div>
            <label className="vp-label">Language</label>
            <select className="vp-input" value={form.language} onChange={(e) => set("language", e.target.value)} data-testid="new-language">
              {(cfg?.languages || []).map((l) => <option key={l}>{l}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="vp-label">Additional instructions</label>
          <textarea className="vp-input" rows={2} value={form.additional_instructions} onChange={(e) => set("additional_instructions", e.target.value)} placeholder="Include market data, avoid jargon, focus on India, …" />
        </div>

        {/* Research mode */}
        <div className="border-t border-[#E4E6EC] pt-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={researchOn} onChange={(e) => setResearchOn(e.target.checked)} data-testid="research-toggle" />
            <Search className="w-3.5 h-3.5 text-[#7C3AED]" />
            <span>Research mode — cite specific sources</span>
          </label>
          {researchOn && (
            <div className="mt-2 space-y-1.5">
              {researchUrls.map((u, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <span className="text-xs text-[#747680] font-mono w-6">[{i+1}]</span>
                  <input
                    className="vp-input py-1.5 text-xs flex-1"
                    value={u}
                    onChange={(e) => setResearchUrls((arr) => arr.map((x, j) => j === i ? e.target.value : x))}
                    placeholder="https://source.example.com/article"
                    data-testid={`research-url-${i+1}`}
                  />
                  <button onClick={() => setResearchUrls((arr) => arr.filter((_, j) => j !== i))} className="p-1 text-[#B00020] hover:bg-[#F1F3F7] rounded" disabled={researchUrls.length === 1}><X className="w-3 h-3" /></button>
                </div>
              ))}
              {researchUrls.length < 5 && (
                <button onClick={() => setResearchUrls((arr) => [...arr, ""])} className="text-xs text-[#7C3AED] hover:underline" data-testid="add-research-url">+ Add source</button>
              )}
              <div className="text-[10px] text-[#747680]">Slides will cite these sources by [n] in speaker notes and store them per-slide.</div>
            </div>
          )}
        </div>

        <div>
          <label className="vp-label">Presentation type</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
            {(cfg?.presentation_types || []).map((t) => (
              <button key={t} type="button" onClick={() => set("presentation_type", t)}
                className={"text-xs py-2 px-3 rounded-lg border text-left " + (form.presentation_type === t ? "border-[#7C3AED] bg-[#7C3AED]/5 text-[#7C3AED]" : "border-[#E4E6EC] hover:border-[#7C3AED]/50")}>
                {t}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="vp-label">Tone</label>
          <div className="flex flex-wrap gap-1.5">
            {(cfg?.tones || []).map((t) => (
              <button key={t} type="button" onClick={() => set("tone", t)}
                className={"text-xs py-1.5 px-3 rounded-full border " + (form.tone === t ? "border-[#7C3AED] bg-[#7C3AED] text-white" : "border-[#E4E6EC] hover:border-[#7C3AED]/50")}>
                {t}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="vp-label">Number of slides</label>
          <div className="flex flex-wrap gap-1.5">
            {SLIDE_QUICK.map((n) => (
              <button key={n} type="button" onClick={() => set("num_slides", n)}
                className={"text-xs py-1.5 px-3 rounded-full border " + (form.num_slides === n ? "border-[#7C3AED] bg-[#7C3AED] text-white" : "border-[#E4E6EC] hover:border-[#7C3AED]/50")}
                data-testid={`slides-${n}`}>{n}</button>
            ))}
            <input type="number" min="3" max="40" className="vp-input py-1.5 px-3 text-xs w-20"
              value={form.num_slides}
              onChange={(e) => set("num_slides", Math.max(3, Math.min(40, parseInt(e.target.value || "8"))))} />
          </div>
        </div>
        <div>
          <label className="vp-label">Design theme</label>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {(cfg?.themes || []).map((t) => (
              <button key={t.id} type="button" onClick={() => set("theme_id", t.id)}
                className={"p-2 rounded-lg border text-left " + (form.theme_id === t.id ? "border-[#7C3AED]" : "border-[#E4E6EC] hover:border-[#7C3AED]/40")}>
                <div className="h-6 rounded mb-1.5 flex overflow-hidden">
                  <div style={{ background: t.primary, width: "40%" }} />
                  <div style={{ background: t.secondary, width: "30%" }} />
                  <div style={{ background: t.accent, width: "30%" }} />
                </div>
                <div className="text-[10px] font-medium">{t.name}</div>
              </button>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.include_speaker_notes} onChange={(e) => set("include_speaker_notes", e.target.checked)} />
          Include speaker notes on every slide
        </label>

        <div className="flex items-center gap-2 flex-wrap pt-1">
          <button onClick={generateOutline} disabled={busy} className="vp-btn-primary flex-1 inline-flex items-center justify-center gap-2 text-base py-3" data-testid="generate-outline-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Generate outline first (recommended)
          </button>
          <button onClick={skipOutlineFullGenerate} disabled={busy} className="vp-btn-ghost text-sm py-3 px-4" data-testid="generate-direct-btn">
            Skip → build full deck
          </button>
        </div>
      </div>
    </div>
  );
}
